import { useEffect, useMemo, useState } from "react";
import { Shield, Plus, Pencil, Trash2, Users as UsersIcon, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPerm, ACTION_LABELS } from "../lib/permissions";

export default function RolesManagement() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modulesMeta, setModulesMeta] = useState({ modules: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role being edited, or "new", or null

  const canCreate = hasPerm(user, "roles", "create");
  const canEdit = hasPerm(user, "roles", "edit");
  const canDelete = hasPerm(user, "roles", "delete");

  const refresh = async () => {
    try {
      const [{ data: r }, { data: m }] = await Promise.all([
        api.get("/roles"),
        api.get("/rbac/modules"),
      ]);
      setRoles(r);
      setModulesMeta(m);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success(`Role "${role.name}" deleted`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="roles-page" className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#00D4FF] mb-1">Role Management</div>
          <h1 className="font-heading text-3xl font-bold">Roles & Permissions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Define what each role can see and do across the platform.
          </p>
        </div>
        {canCreate && (
          <button
            data-testid="new-role-btn"
            onClick={() => setEditing("new")}
            className="flex items-center gap-2 bg-[#00D4FF] text-[#0A0B10] px-4 py-2 text-sm font-semibold tracking-wider uppercase hover:bg-[#00B8D4] transition-colors"
          >
            <Plus size={16} /> New Role
          </button>
        )}
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading roles…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.map((r) => (
            <RoleCard
              key={r.id}
              role={r}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => setEditing(r)}
              onDelete={() => handleDelete(r)}
            />
          ))}
        </div>
      )}

      {editing && (
        <RoleEditor
          modulesMeta={modulesMeta}
          role={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function RoleCard({ role, canEdit, canDelete, onEdit, onDelete }) {
  const pct = role.total ? Math.round((role.granted / role.total) * 100) : 0;
  return (
    <div
      data-testid={`role-card-${role.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-[#12141D] border border-border p-5 flex flex-col gap-4 hover:border-[#00D4FF]/40 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 flex items-center justify-center border ${role.is_system ? "border-amber-500/40 text-amber-400" : "border-[#00D4FF]/40 text-[#00D4FF]"}`}>
          <Shield size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold truncate">{role.name}</div>
            {role.is_system && (
              <span className="text-[9px] tracking-[0.2em] uppercase bg-amber-500/10 text-amber-400 border border-amber-500/30 px-1.5 py-0.5">
                System
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {role.description || "—"}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
          <span>Permissions</span>
          <span>{role.granted}/{role.total} ({pct}%)</span>
        </div>
        <div className="h-1.5 bg-[#1A1C28] overflow-hidden">
          <div
            className={`h-full ${role.is_system && role.name === "Administrator" ? "bg-amber-400" : "bg-[#00D4FF]"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <UsersIcon size={13} /> {role.user_count} {role.user_count === 1 ? "user" : "users"}
        </span>
        <div className="flex gap-1.5">
          {canEdit && (
            <button
              data-testid={`edit-role-${role.id}`}
              onClick={onEdit}
              className="p-1.5 text-muted-foreground hover:text-[#00D4FF] hover:bg-[#1A1C28] transition-colors"
              title="Edit role"
            >
              <Pencil size={14} />
            </button>
          )}
          {canDelete && !role.is_system && (
            <button
              data-testid={`delete-role-${role.id}`}
              onClick={onDelete}
              className="p-1.5 text-muted-foreground hover:text-red-400 hover:bg-[#1A1C28] transition-colors"
              title="Delete role"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleEditor({ modulesMeta, role, onClose, onSaved }) {
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [perms, setPerms] = useState(() => {
    const base = {};
    modulesMeta.modules.forEach((m) => { base[m.key] = []; });
    if (role?.permissions) {
      Object.keys(role.permissions).forEach((k) => { base[k] = [...(role.permissions[k] || [])]; });
    }
    return base;
  });
  const [saving, setSaving] = useState(false);

  const total = modulesMeta.total || 0;
  const granted = useMemo(
    () => Object.values(perms).reduce((s, arr) => s + arr.length, 0),
    [perms]
  );

  const togglePerm = (mk, action) => {
    setPerms((p) => {
      const cur = new Set(p[mk] || []);
      if (cur.has(action)) cur.delete(action); else cur.add(action);
      return { ...p, [mk]: Array.from(cur) };
    });
  };

  const toggleAllForModule = (m, on) => {
    setPerms((p) => ({ ...p, [m.key]: on ? [...m.actions] : [] }));
  };

  const selectAll = () => {
    const next = {};
    modulesMeta.modules.forEach((m) => { next[m.key] = [...m.actions]; });
    setPerms(next);
  };

  const clearAll = () => {
    const next = {};
    modulesMeta.modules.forEach((m) => { next[m.key] = []; });
    setPerms(next);
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Role name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), permissions: perms };
      if (role) {
        await api.put(`/roles/${role.id}`, payload);
        toast.success(`Role "${name}" updated`);
      } else {
        await api.post("/roles", payload);
        toast.success(`Role "${name}" created`);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 sm:p-6 lg:pl-[17rem]"
      onClick={onClose}
    >
      <div
        data-testid="role-editor-modal"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#12141D] border border-border w-full max-w-4xl flex flex-col max-h-[calc(100dvh-3rem)] max-h-[calc(100vh-3rem)]"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="font-heading text-xl font-bold">{role ? "Edit Role" : "New Role"}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-white" data-testid="close-role-editor">
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">Role Name *</label>
              <input
                data-testid="role-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!!role?.is_system}
                className="w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF] disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="e.g. QC Inspector"
              />
              {role?.is_system && (
                <p className="text-[10px] text-amber-400/80 mt-1">System role name is locked. You can still edit description &amp; permissions.</p>
              )}
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">Description</label>
              <input
                data-testid="role-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF]"
                placeholder="Short description"
              />
            </div>
          </div>

          <div className="px-6 pb-2 flex items-center justify-between">
            <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
              Permissions ({granted}/{total})
            </div>
            <div className="text-xs flex gap-3">
              <button onClick={selectAll} className="text-[#00D4FF] hover:underline" data-testid="role-select-all">Select All</button>
              <span className="text-muted-foreground">|</span>
              <button onClick={clearAll} className="text-muted-foreground hover:text-white" data-testid="role-clear-all">Clear All</button>
            </div>
          </div>

          <div className="px-6 pb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
                  <th className="text-left py-2 font-normal">Module</th>
                  {["view_all", "view_own", "create", "edit", "delete", "upload"].map((a) => (
                    <th key={a} className="py-2 font-normal text-center w-20">{ACTION_LABELS[a]}</th>
                  ))}
                  <th className="py-2 font-normal text-center w-16">All</th>
                </tr>
              </thead>
              <tbody>
                {modulesMeta.modules.map((m) => {
                  const allOn = m.actions.length > 0 && m.actions.every((a) => (perms[m.key] || []).includes(a));
                  return (
                    <tr key={m.key} className="border-b border-border/40">
                      <td className="py-2.5">{m.label}</td>
                      {["view_all", "view_own", "create", "edit", "delete", "upload"].map((a) => {
                        const supported = m.actions.includes(a);
                        const checked = (perms[m.key] || []).includes(a);
                        return (
                          <td key={a} className="text-center">
                            {supported ? (
                              <button
                                data-testid={`perm-${m.key}-${a}`}
                                onClick={() => togglePerm(m.key, a)}
                                className={`h-5 w-5 inline-flex items-center justify-center border ${checked ? "bg-[#00D4FF] border-[#00D4FF] text-[#0A0B10]" : "border-border bg-[#0A0B10] text-transparent hover:border-[#00D4FF]/60"}`}
                              >
                                <Check size={12} />
                              </button>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center">
                        <button
                          data-testid={`perm-all-${m.key}`}
                          onClick={() => toggleAllForModule(m, !allOn)}
                          className={`h-5 w-5 inline-flex items-center justify-center border ${allOn ? "bg-amber-400 border-amber-400 text-[#0A0B10]" : "border-border bg-[#0A0B10] text-transparent hover:border-amber-400/60"}`}
                        >
                          <Check size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 shrink-0 bg-[#12141D]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-border hover:bg-[#1A1C28] transition-colors"
            data-testid="cancel-role-btn"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-role-btn"
            className="px-4 py-2 text-sm bg-[#00D4FF] text-[#0A0B10] font-semibold tracking-wider uppercase hover:bg-[#00B8D4] disabled:opacity-50"
          >
            {saving ? "Saving…" : role ? "Save Changes" : "Create Role"}
          </button>
        </footer>
      </div>
    </div>
  );
}
